import { useEffect, useMemo, useRef } from 'react';
import { MARKER_TYPES } from '../constants';

/**
 * Search management hook
 * Handles all search-related logic and state
 */

export function useExploreSearch(state, dispatch, initialMarkers, initialListItems, filterMarkers) {
  const searchDataset = useMemo(() => state.viewMode === 'map' ? initialMarkers : initialListItems, [state.viewMode, initialMarkers, initialListItems]);

  // Set search query
  const setSearchQuery = query => {
    dispatch({
      type: 'SET_SEARCH_QUERY',
      payload: query
    });
  };

  // Clear search and revert to original map state
  const clearSearch = () => {
    dispatch({
      type: 'RESET_SEARCH'
    });
  };

  // Commit search - called when user selects from dropdown
  // This triggers filtering in List/Photos views
  const commitSearch = () => {
    dispatch({
      type: 'SET_SEARCH_COMMITTED',
      payload: true
    });
  };
  const lastCommittedSearchKeyRef = useRef('');

  // Run committed search when loading from URL (no user interaction)
  useEffect(() => {
    if (!state.searchCommitted) return;
    if (!state.searchQuery) return;
    if (searchDataset.length === 0) return;
    const searchKey = `${state.viewMode}:${state.searchQuery}`;
    if (lastCommittedSearchKeyRef.current === searchKey) return;
    lastCommittedSearchKeyRef.current = searchKey;
    search(state.searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.searchCommitted, state.searchQuery, searchDataset, state.viewMode]);

  // Perform search - does NOT filter markers, just stores search results for autofill
  // Zooming/centering is handled by SearchInput when user selects an item
  const search = async query => {
    if (!query.trim()) {
      clearSearch();
      return;
    }
    dispatch({
      type: 'SET_LOADING',
      payload: true
    });
    dispatch({
      type: 'SET_ERROR',
      payload: null
    });
    try {
      const allMarkers = searchDataset || [];

      // Find matching items for search results (used by autofill)
      const searchResults = allMarkers.filter(marker => marker.name.toLowerCase().includes(query.toLowerCase()) || marker.description?.toLowerCase().includes(query.toLowerCase()) || marker.location?.address?.toLowerCase().includes(query.toLowerCase()));

      // SPECIAL HANDLING: When both hearts and artists toggles are ON
      // If search found artists, show their corresponding hearts instead (artists are embedded)
      // This applies to ALL views (map, list, photos)
      const bothTogglesOn = state.filters.hearts && state.filters.artists;
      let resultsToDisplay = searchResults;
      if (bothTogglesOn) {
        // Separate artists from other results
        const artistResults = searchResults.filter(m => m.type === MARKER_TYPES.ARTIST);
        const nonArtistResults = searchResults.filter(m => m.type !== MARKER_TYPES.ARTIST);

        // For each artist, find the corresponding heart
        const heartsFromArtists = [];
        artistResults.forEach(artist => {
          if (artist.heartSlug) {
            // Find the heart by slug
            const correspondingHeart = allMarkers.find(m => (m.type === MARKER_TYPES.HEART || m.type === MARKER_TYPES.SAVED_HEART || m.type === MARKER_TYPES.VISITED_HEART || m.type === MARKER_TYPES.SAVED_VISITED_HEART) && m.slug === artist.heartSlug);
            if (correspondingHeart && !heartsFromArtists.find(h => h.id === correspondingHeart.id)) {
              heartsFromArtists.push(correspondingHeart);
            }
          }
        });

        // Combine: hearts (from artists) + other non-artist results
        resultsToDisplay = [...heartsFromArtists, ...nonArtistResults];
      }

      // Apply active filters to search results
      // For photos/list views: pass 'list' to ensure both hearts and artists can appear in results
      // For map view: pass 'map' to hide duplicate artist pins
      const viewModeForFilter = state.viewMode === 'map' ? 'map' : 'list';
      const filteredResults = filterMarkers(resultsToDisplay, state.filters, viewModeForFilter, false);
      dispatch({
        type: 'SET_SEARCH_RESULTS',
        payload: filteredResults
      });
      // Keep all markers visible - don't filter the map markers based on search
      // dispatch({ type: 'SET_MARKERS', payload: filteredResults });
    } catch (error) {
      //console.error('Search error:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: error.message
      });
    } finally {
      dispatch({
        type: 'SET_LOADING',
        payload: false
      });
    }
  };
  return {
    searchQuery: state.searchQuery,
    searchResults: state.searchResults,
    isLoading: state.isLoading,
    error: state.error,
    setSearchQuery,
    search,
    clearSearch,
    commitSearch
  };
}